import React, { useEffect } from "react";
import { useAuthState } from "@src/hooks/useAuth";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import AppInput from "@src/components/AppInput";
import TimesIcon from "@assets/img/times-icon.svg";
import RightArrow from "@assets/img/right-arrow.svg";
import { theme } from "@src/theme";
import styled from "styled-components";
import Spinner from "@src/components/Spinner";
import { SubmitButton } from "@src/components/SubmitButton";
import { CloseButton } from "@src/components/CloseButton";
import {
  useCreateInvoice,
  useUpdateInvoice,
  useSubmitInvoice,
} from "@src/queries/invoiceQueries";
import { useNotification } from "@src/context/NotificationContext";
import { CreateInvoicePayload, IInvoice } from "@src/types/invoice";
import Tooltip from "@src/components/Tooltip";
import {
  useMyBillingInfo,
  useUpsertMyBillingInfo,
} from "@src/queries/billingInformationQueries";
import { UpsertBillingInformationPayload } from "@src/types/billingInformation";

const ActionsButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  min-height: 50px;
  margin-bottom: ${theme.margins.medium};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.primary};
  margin-top: ${theme.margins.medium};
  margin-bottom: ${theme.margins.xl};
  border-bottom: 1px solid ${theme.colors.gray200};
  padding-bottom: 6px;
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${theme.margins.xsmall};
  flex-direction: column;

  & > div {
    flex: 1;
    min-width: 200px;
  }
`;

const schema = yup.object().shape({
  // Invoice fields
  invoiceNumber: yup.string().required("Required"),
  playlistCount: yup
    .number()
    .typeError("Must be a number")
    .min(0)
    .required("Required"),
  pricePerPlaylist: yup
    .number()
    .typeError("Must be a number")
    .min(0)
    .required("Required"),
  totalPrice: yup.number().required(),
  timestamp: yup.string().required("Required"),

  // Billing fields
  legalName: yup.string().required("Required"),
  emailAddress: yup.string().email("Invalid email").required("Required"),
  phoneNumber: yup.string().required("Required"),
  streetAddress: yup.string().required("Required"),
  state: yup.string().required("Required"),
  country: yup.string().required("Required"),
  zipCode: yup.string().required("Required"),
  bankName: yup.string().required("Required"),
  bankAddress: yup.string().required("Required"),
  accountNumber: yup.string().required("Required"),
  swiftCode: yup.string().required("Required"),
  iban: yup.string().optional().default(""),
  routingNumber: yup.string().optional().default(""),
  sortCode: yup.string().optional().default(""),
  vatNumber: yup.string().optional().default(""),
});

export type UnifiedInvoiceFormValues = CreateInvoicePayload &
  UpsertBillingInformationPayload;

const defaultValues: UnifiedInvoiceFormValues = {
  invoiceNumber: "",
  playlistCount: 0,
  pricePerPlaylist: 0,
  totalPrice: 0,
  timestamp: "",
  invoiceStatus: "submitted",
  legalName: "",
  emailAddress: "",
  phoneNumber: "",
  streetAddress: "",
  state: "",
  country: "",
  zipCode: "",
  bankName: "",
  bankAddress: "",
  accountNumber: "",
  swiftCode: "",
  iban: "",
  routingNumber: "",
  sortCode: "",
  vatNumber: "",
};

interface InvoiceFormProps {
  onSuccess?: () => void;
  invoice?: IInvoice | null;
}

const InvoiceForm = ({
  onSuccess,
  invoice,
}: InvoiceFormProps): React.JSX.Element => {
  const { role } = useAuthState();
  const isAdminOrManager = role?.type === "admin" || role?.type === "manager";

  const { data: billingInfo } = useMyBillingInfo({
    enabled: !isAdminOrManager,
  });
  const { mutateAsync: upsertBillingInfo, isPending: isUpsertingBilling } =
    useUpsertMyBillingInfo();
  const memoDefaultValues = React.useMemo(() => {
    if (billingInfo && !invoice) {
      return {
        ...defaultValues,
        legalName: billingInfo.legalName || "",
        emailAddress: billingInfo.emailAddress || "",
        phoneNumber: billingInfo.phoneNumber || "",
        streetAddress: billingInfo.streetAddress || "",
        state: billingInfo.state || "",
        country: billingInfo.country || "",
        zipCode: billingInfo.zipCode || "",
        bankName: billingInfo.bankName || "",
        bankAddress: billingInfo.bankAddress || "",
        accountNumber: billingInfo.accountNumber || "",
        swiftCode: billingInfo.swiftCode || "",
        iban: billingInfo.iban || "",
        routingNumber: billingInfo.routingNumber || "",
        sortCode: billingInfo.sortCode || "",
        vatNumber: billingInfo.vatNumber || "",
      };
    }
    return defaultValues;
  }, [billingInfo, invoice]);

  const hasInitialized = React.useRef(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    register,
    formState: { errors, isDirty },
  } = useForm<UnifiedInvoiceFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: memoDefaultValues,
  });

  const createInvoiceMutation = useCreateInvoice();
  const updateInvoiceMutation = useUpdateInvoice();
  const submitInvoiceMutation = useSubmitInvoice();
  const { addNotification } = useNotification();
  const isEditing = !!invoice;
  useEffect(() => {
    if (invoice) {
      if (!hasInitialized.current) {
        reset({
          ...defaultValues,
          invoiceNumber: invoice.invoiceNumber,
          playlistCount: invoice.playlistCount,
          pricePerPlaylist: invoice.pricePerPlaylist,
          totalPrice: invoice.totalPrice,
          timestamp: new Date(invoice.timestamp).toISOString().slice(0, 16),
          invoiceStatus: invoice.invoiceStatus,
          legalName: invoice.billingSnapshot?.legalName || "",
          emailAddress: invoice.billingSnapshot?.emailAddress || "",
          phoneNumber: invoice.billingSnapshot?.phoneNumber || "",
          streetAddress: invoice.billingSnapshot?.streetAddress || "",
          state: invoice.billingSnapshot?.state || "",
          country: invoice.billingSnapshot?.country || "",
          zipCode: invoice.billingSnapshot?.zipCode || "",
          bankName: invoice.billingSnapshot?.bankName || "",
          bankAddress: invoice.billingSnapshot?.bankAddress || "",
          accountNumber: invoice.billingSnapshot?.accountNumber || "",
          swiftCode: invoice.billingSnapshot?.swiftCode || "",
          iban: invoice.billingSnapshot?.iban || "",
          routingNumber: invoice.billingSnapshot?.routingNumber || "",
          sortCode: invoice.billingSnapshot?.sortCode || "",
          vatNumber: invoice.billingSnapshot?.vatNumber || "",
        });
        hasInitialized.current = true;
      }
    } else if (billingInfo && !isDirty) {
      reset(memoDefaultValues);
    }
  }, [billingInfo, invoice, reset, isDirty, memoDefaultValues]);

  const watchPlaylistCount = watch("playlistCount");
  const watchPricePerPlaylist = watch("pricePerPlaylist");

  useEffect(() => {
    const total =
      Number(watchPlaylistCount || 0) * Number(watchPricePerPlaylist || 0);
    setValue("totalPrice", total);
  }, [watchPlaylistCount, watchPricePerPlaylist, setValue]);

  useEffect(() => {
    if (!billingInfo || invoice) return;

    const currentValues = getValues();
    const currentRoutingNumber = currentValues.routingNumber?.trim() || "";
    const currentSortCode = currentValues.sortCode?.trim() || "";
    const billingRoutingNumber = billingInfo.routingNumber?.trim() || "";
    const billingSortCode = billingInfo.sortCode?.trim() || "";

    const shouldHydrateRoutingNumber =
      !currentRoutingNumber && !!billingRoutingNumber;
    const shouldHydrateSortCode = !currentSortCode && !!billingSortCode;

    if (!shouldHydrateRoutingNumber && !shouldHydrateSortCode) return;

    reset(
      {
        ...currentValues,
        routingNumber: shouldHydrateRoutingNumber
          ? billingInfo.routingNumber || ""
          : currentValues.routingNumber,
        sortCode: shouldHydrateSortCode
          ? billingInfo.sortCode || ""
          : currentValues.sortCode,
      },
      {
        keepDirty: true,
        keepTouched: true,
      }
    );
  }, [
    billingInfo?.routingNumber,
    billingInfo?.sortCode,
    invoice,
    getValues,
    reset,
  ]);

  const onSubmit = async (data: UnifiedInvoiceFormValues) => {
    try {
      // 1. Upsert billing information
      const billingPayload: UpsertBillingInformationPayload = {
        legalName: data.legalName,
        emailAddress: data.emailAddress,
        phoneNumber: data.phoneNumber,
        streetAddress: data.streetAddress,
        state: data.state,
        country: data.country,
        zipCode: data.zipCode,
        bankName: data.bankName,
        bankAddress: data.bankAddress,
        accountNumber: data.accountNumber,
        swiftCode: data.swiftCode,
        iban: data.iban,
        routingNumber: data.routingNumber,
        sortCode: data.sortCode,
        vatNumber: data.vatNumber,
      };

      if (!isAdminOrManager) {
        await upsertBillingInfo(billingPayload);
      }

      // 2. Create invoice with billingSnapshot
      const invoicePayload: CreateInvoicePayload = {
        invoiceNumber: data.invoiceNumber,
        playlistCount: data.playlistCount,
        pricePerPlaylist: data.pricePerPlaylist,
        totalPrice: data.totalPrice,
        timestamp: data.timestamp,
        invoiceStatus: data.invoiceStatus || "submitted",
        billingSnapshot: billingPayload,
      };

      if (isEditing && invoice?.documentId) {
        updateInvoiceMutation.mutate(
          {
            documentId: invoice.documentId,
            payload: invoicePayload,
          },
          {
            onSuccess: () => {
              addNotification("Invoice updated successfully", "success");
              if (onSuccess) onSuccess();
            },
            onError: (error: any) => {
              const message =
                error?.response?.data?.error?.message ||
                "Failed to update invoice";
              addNotification(message, "error");
            },
          }
        );
      } else {
        createInvoiceMutation.mutate(
          { ...invoicePayload, invoiceStatus: "submitted" },
          {
            onSuccess: (response) => {
              const documentId = response.data.data.documentId;
              addNotification("Invoice created. Generating PDF...", "success");

              // Trigger PDF generation
              submitInvoiceMutation.mutate(documentId, {
                onSuccess: () => {
                  if (onSuccess) onSuccess();
                  hasInitialized.current = false;
                  reset(memoDefaultValues);
                },
                onError: () => {
                  addNotification("Failed to trigger PDF generation", "error");
                  if (onSuccess) onSuccess();
                  hasInitialized.current = false;
                  reset(memoDefaultValues);
                },
              });
            },
            onError: (error: any) => {
              const message =
                error?.response?.data?.error?.message ||
                "Failed to create invoice";
              addNotification(message, "error");
            },
          }
        );
      }
    } catch {
      addNotification("Failed to update billing profile", "error");
    }
  };

  const onInvalid = () => {
    addNotification("Please check the form for required fields", "error");
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <input type="hidden" {...register("invoiceStatus")} />
      <ActionsButtonGroup>
        <CloseButton
          type="button"
          onClick={() => {
            if (onSuccess) onSuccess();
            hasInitialized.current = false;
            reset(memoDefaultValues);
          }}
        >
          <img
            src={TimesIcon}
            alt="X"
            style={{ width: "10px", height: "10px" }}
          />
        </CloseButton>
        {(createInvoiceMutation.isPending ||
          updateInvoiceMutation.isPending) && <Spinner small />}
        <Tooltip
          content={isEditing ? "Save Changes" : "Create Invoice"}
          position="top"
        >
          <SubmitButton
            disabled={
              createInvoiceMutation.isPending ||
              updateInvoiceMutation.isPending ||
              submitInvoiceMutation.isPending ||
              isUpsertingBilling ||
              !isDirty
            }
            type="submit"
          >
            {submitInvoiceMutation.isPending ? (
              <Spinner small />
            ) : (
              <img
                src={RightArrow}
                alt="Submit"
                style={{ height: "50%", objectFit: "contain" }}
              />
            )}
          </SubmitButton>
        </Tooltip>
      </ActionsButtonGroup>
      <SectionTitle>Billing Profile</SectionTitle>
      <FormRow>
        <AppInput
          name="legalName"
          label="Legal Entity Name"
          control={control}
          placeholder="Legal Entity Name"
          errors={errors}
        />
        <AppInput
          name="emailAddress"
          label="Email Address"
          type="email"
          control={control}
          placeholder="Email Address"
          errors={errors}
        />
      </FormRow>
      <FormRow>
        <AppInput
          name="phoneNumber"
          label="Phone Number"
          control={control}
          placeholder="Phone Number"
          errors={errors}
        />
        <AppInput
          name="vatNumber"
          label="VAT Number (Optional)"
          control={control}
          placeholder="VAT Number"
          errors={errors}
        />
      </FormRow>

      <SectionTitle>Address</SectionTitle>
      <FormRow>
        <AppInput
          name="streetAddress"
          label="Street Address"
          control={control}
          placeholder="Street Address"
          errors={errors}
        />
        <AppInput
          name="state"
          label="State / Province"
          control={control}
          placeholder="State / Province"
          errors={errors}
        />
      </FormRow>
      <FormRow>
        <AppInput
          name="country"
          label="Country"
          control={control}
          placeholder="Country"
          errors={errors}
        />
        <AppInput
          name="zipCode"
          label="ZIP / Postal Code"
          control={control}
          placeholder="ZIP / Postal Code"
          errors={errors}
        />
      </FormRow>

      <SectionTitle>Bank Details</SectionTitle>
      <FormRow>
        <AppInput
          name="bankName"
          label="Bank Name"
          control={control}
          placeholder="Bank Name"
          errors={errors}
        />
        <AppInput
          name="bankAddress"
          label="Bank Address"
          control={control}
          placeholder="Bank Address"
          errors={errors}
        />
      </FormRow>
      <FormRow>
        <AppInput
          name="accountNumber"
          label="Account Number"
          control={control}
          placeholder="Account Number"
          errors={errors}
        />
        <AppInput
          name="swiftCode"
          label="SWIFT / BIC (All banks)"
          control={control}
          placeholder="SWIFT / BIC (All banks)"
          errors={errors}
        />
      </FormRow>
      <FormRow>
        <AppInput
          name="iban"
          label="IBAN (Non-USA banks)"
          control={control}
          placeholder="IBAN (Non-USA banks)"
          errors={errors}
        />
      </FormRow>
      <FormRow>
        <AppInput
          name="routingNumber"
          label="Routing Number (USA only)"
          control={control}
          placeholder="Routing Number"
          errors={errors}
        />
        <AppInput
          name="sortCode"
          label="Sort Code (UK only)"
          control={control}
          placeholder="Sort Code"
          errors={errors}
        />
      </FormRow>

      <SectionTitle>General Information</SectionTitle>
      <FormRow>
        <AppInput
          name="invoiceNumber"
          label="Invoice Number"
          control={control}
          placeholder="Enter invoice number"
          errors={errors}
        />
        <AppInput
          name="timestamp"
          label="Date"
          type="datetime-local"
          control={control}
          placeholder="Select date"
          errors={errors}
        />
      </FormRow>

      <SectionTitle>Billing Items</SectionTitle>
      <FormRow>
        <AppInput
          name="playlistCount"
          label="Playlist Count"
          type="number"
          min="0"
          control={control}
          placeholder="0"
          errors={errors}
        />
        <AppInput
          name="pricePerPlaylist"
          label="Price Per Playlist"
          type="number"
          icon="$"
          min="0"
          control={control}
          placeholder="0.00"
          errors={errors}
        />
        <AppInput
          name="totalPrice"
          label="Total Price"
          type="number"
          icon="$"
          disabled
          control={control}
          placeholder="0.00"
          errors={errors}
        />
      </FormRow>
    </form>
  );
};

export default InvoiceForm;
